// @owners { team: patients-team }
import { COLORS } from '@alto/design-library-tokens';
import { ActionSheetContext, Body, Button, Card, type DateData, NavBarLogo, XsSpacing } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { updateCycleAttributes } from '~shared/actions/ui/progynyWizard';
import progynyLogo from '~shared/assets/images/progyny_by_alto_logo.png';
import getLoading from '~shared/features/ui/selectors/getLoading';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import {
  getProgynyWizardCycle,
  getProgynyWizardFirstFill,
  getProgynyWizardUpdateAttributes,
} from '~shared/selectors/checkout/progyny';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { getProgynyWizardNextRoute } from '../helpers/getProgynyWizardNextRoute';
import { DatePicker } from './DatePicker';
import { ProgynyWizardPage } from './ProgynyWizardPage';
import { ProgynyWizardStepsHeaderContent } from './ProgynyWizardStepsHeaderContent';
import { StartDateInfoActionSheet } from './StartDateInfoActionSheet';

export const StimStartDate = () => {
  const dispatch = useDispatchShared();
  const cycle = useSelectorShared(getProgynyWizardCycle);
  const updateAttributes = useSelectorShared(getProgynyWizardUpdateAttributes);
  const originalStartDate = cycle?.stim_meds_start_date ?? '';
  const lastSubmittedStartDate = updateAttributes?.stim_meds_start_date;
  const initialStartDate = lastSubmittedStartDate ?? originalStartDate;

  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const { updateFertilityCycleLoading } = useSelectorShared(getLoading);
  const { getCurrentRouteName, navigate, goBack } = useNavigation();
  const currentRoute = getCurrentRouteName();
  const { trackPageView } = useAnalytics();
  const nextRouteName = getProgynyWizardNextRoute(currentRoute);
  const isRefill = !useSelectorShared(getProgynyWizardFirstFill);
  const [startDate, setStartDate] = useState(initialStartDate);

  useEffect(() => {
    trackPageView({
      event: EVENTS.MEDICATION_START_DATES_VIEWED,
      params: { is_refill: isRefill },
    });
  }, [isRefill, trackPageView]);

  const noValues = !startDate && !initialStartDate;
  const noChanges = startDate === initialStartDate;

  const handleOpenStartDateInfoActionSheet = useCallback(() => {
    setActiveActionSheet(<StartDateInfoActionSheet />);
  }, [setActiveActionSheet]);

  const handleDayPress = useCallback((day: DateData) => {
    setStartDate(day.dateString);
  }, []);

  const handleClearDate = useCallback(() => {
    setStartDate('');
  }, []);

  const handleSkip = useCallback(() => {
    if (nextRouteName) navigate(nextRouteName);
  }, [navigate, nextRouteName]);

  const handleSave = useCallback(() => {
    dispatch(
      updateCycleAttributes({
        stim_meds_start_date: startDate,
      }),
    );

    if (nextRouteName) navigate(nextRouteName);
  }, [dispatch, navigate, nextRouteName, startDate]);

  return (
    <ProgynyWizardPage
      dismissIcon="chevronleft"
      onDismiss={goBack}
      headerBackgroundColor={COLORS.BACKGROUND_COLORS.PINK_LIGHTEST}
      NavBarCenterContent={<NavBarLogo source={progynyLogo} />}
      HeaderContent={
        <ProgynyWizardStepsHeaderContent
          headline={"What's your approximate injection start date?"}
          subheadline={
            <Body>
              If you <Body fontFamily="semibold">don't know</Body> yet, that's fine — estimate it or add the date of
              your next doctor's appointment.
            </Body>
          }
          linkCopy="Why are you asking for this?"
          linkOnPress={handleOpenStartDateInfoActionSheet}
        />
      }
      buttons={[
        noChanges ? (
          <Button
            label={noValues ? 'Skip' : 'Continue'}
            onPress={handleSkip}
            type={noValues ? 'tertiary' : 'primary'}
            loading={updateFertilityCycleLoading}
            testID="e2e-progyny-start-date-skip-button"
          />
        ) : (
          <Button
            label="Next"
            onPress={handleSave}
            loading={updateFertilityCycleLoading}
            testID="e2e-progyny-start-date-skip-button"
          />
        ),
      ]}
    >
      <Card>
        <DatePicker
          current={startDate}
          markedDates={startDate ? { [startDate]: { selected: true } } : undefined}
          onDayPress={handleDayPress}
        />
      </Card>
      {!!startDate && (
        <>
          <XsSpacing />
          <Button
            label="Clear Date"
            onPress={handleClearDate}
            type="tertiary"
          />
        </>
      )}
    </ProgynyWizardPage>
  );
};
